import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useGetLookupQuery, useUpdateBatchMutation } from '../../api/gridApi';
import type { ColumnMetadata } from '../../types';
import { extractErrorMessage } from '../../../../utils/errorUtils';

type EmployeeManagerDialogProps = {
  open: boolean;
  title?: string | null;
  nodeKey: string;
  employeeIds: number[];
  managerFieldName: string;
  managerFieldKey: string;
  currentManagerIds: number[];
  defaultFilters?: ColumnMetadata['defaultFilters'];
  onClose: () => void;
  onSuccess: (message: string) => void;
};

export const EmployeeManagerDialog = ({
  open,
  title,
  nodeKey,
  employeeIds,
  managerFieldName,
  managerFieldKey,
  currentManagerIds,
  defaultFilters,
  onClose,
  onSuccess,
}: EmployeeManagerDialogProps) => {
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const lookup = useGetLookupQuery(
    {
      nodeKey,
      fieldKey: managerFieldKey,
      displayFieldName: 'CrmRelationName',
      defaultFilters,
      forDropdown: true,
    },
    { skip: !open || !nodeKey || !managerFieldKey }
  );
  const [updateBatch, updateState] = useUpdateBatchMutation();

  const managers = useMemo(
    () =>
      (lookup.data?.array ?? [])
        .map((item) => {
          const id = Number(item.Id);
          const name =
            item.Fields?.find((field) => field.Name === 'CrmRelationName')?.StringValue ??
            item.DisplayText ??
            String(id);
          return Number.isInteger(id) && id > 0 ? { id, name } : null;
        })
        .filter((manager): manager is { id: number; name: string } => manager != null)
        .filter((manager) => !employeeIds.includes(manager.id)),
    [employeeIds, lookup.data?.array]
  );
  const currentManagerId = currentManagerIds.length === 1 ? currentManagerIds[0] : null;
  const errorMessage = lookup.error
    ? extractErrorMessage(lookup.error, 'Failed to load manager options.')
    : updateState.error
      ? extractErrorMessage(updateState.error, 'Failed to update employee manager.')
      : validationMessage;

  const handleApply = async () => {
    if (!selectedManagerId) {
      setValidationMessage('Please select a manager.');
      return;
    }

    setValidationMessage(null);
    try {
      await updateBatch({
        nodeKey,
        keys: employeeIds.map(String),
        fields: [{ FieldName: managerFieldName, StringValue: selectedManagerId }],
      }).unwrap();
      onSuccess(`Manager updated for ${employeeIds.length} employee(s).`);
    } catch {
      // The mutation error is rendered in the dialog.
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title || 'Assign/change manager'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">Selected employees: {employeeIds.length}</Typography>
          {currentManagerIds.length > 1 && (
            <Alert severity="info">
              Current managers differ between selected employees. Choose one manager to apply to
              all.
            </Alert>
          )}
          {currentManagerIds.length <= 1 && (
            <Typography variant="body2" color="text.secondary">
              Current manager:{' '}
              {currentManagerId
                ? (managers.find((manager) => manager.id === currentManagerId)?.name ??
                  String(currentManagerId))
                : 'No manager is currently assigned'}
            </Typography>
          )}
          {lookup.isLoading && (
            <Stack alignItems="center" spacing={1} py={2}>
              <CircularProgress size={24} />
              <Typography variant="body2">Loading managers...</Typography>
            </Stack>
          )}
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <FormControl fullWidth disabled={lookup.isLoading || updateState.isLoading}>
            <InputLabel id="employee-manager-label">Manager</InputLabel>
            <Select
              labelId="employee-manager-label"
              value={selectedManagerId}
              label="Manager"
              onChange={(event) => {
                setValidationMessage(null);
                setSelectedManagerId(event.target.value);
              }}
            >
              {managers.map((manager) => (
                <MenuItem key={manager.id} value={manager.id}>
                  {manager.name}
                  {manager.id === currentManagerId ? ' (current)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updateState.isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={lookup.isLoading || updateState.isLoading || managers.length === 0}
        >
          {updateState.isLoading ? <CircularProgress size={20} /> : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeManagerDialog;
